import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import Config from './Config.ts';
import { IS_EMBED } from './isEmbed.ts';

declare const __DORSIA_WIDGET_VERSION__: string;

const SENTRY_DSN: string =
  (import.meta.env.VITE_SENTRY_DSN as string | undefined) ||
  'https://e649d46018280c4947277b6641542e4e@o1071572.ingest.sentry.io/4506084937170944';

let initialized = false;

export function initSentry(surface: 'spa' | 'embed') {
  if (initialized) return;
  initialized = true;

  const enabled = Config.data.environment !== 'local';
  const release =
    typeof __DORSIA_WIDGET_VERSION__ !== 'undefined' ? __DORSIA_WIDGET_VERSION__ : undefined;

  if (IS_EMBED) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: Config.data.environment,
      enabled,
      release,
      tracesSampleRate: 0.05,
      initialScope: {
        tags: {
          surface,
          partner_origin: window.location.origin,
        },
      },
      // The embed runs on partner pages, where Sentry's default global handlers
      // (window.onerror / unhandledrejection) would otherwise capture the HOST
      // page's own uncaught errors as surface:embed — and, once the widget calls
      // Sentry.setUser, attach diner identity to unrelated partner errors. Only
      // report events that originate from the widget bundle: keep an event if a
      // stack frame references `dorsia-widget`, or if it has no exception frames
      // at all (a pure captureMessage — only Dorsia code calls this SDK instance;
      // partner code never does). Everything else (host-page noise) is dropped.
      beforeSend(event) {
        const frames = (event.exception?.values ?? []).flatMap(
          (value) => value.stacktrace?.frames ?? []
        );
        if (frames.length === 0) return event;
        const fromWidget = frames.some(
          (frame) => typeof frame.filename === 'string' && frame.filename.includes('dorsia-widget')
        );
        return fromWidget ? event : null;
      },
    });
  } else {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: Config.data.environment,
      enabled,
      release,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration(),
      ],
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/10.0.0.\d:\d+\//,
        /^https:\/\/dorsia-booking.pages.dev\//,
        /^https:\/\/staging.dorsia-booking.pages.dev\//,
        /^https:\/\/book.dorsia.com\//,
        /^https:\/\/booking.dorsia.com\//,
        /^https:\/\/booking-web.*\.vercel\.app\//,
      ],
      tracesSampleRate: 0.8,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  }
}
